import React, {
	createContext,
	useCallback,
	useEffect,
	useMemo,
	useState,
	useContext,
} from "react";
import { useMsal } from "@azure/msal-react";
import { UserContext } from "./UserContext";
import { getAPICall, putAPICall } from "../config/apiCalls";

export const UserPreferencesContext = createContext({
	loading: true,
	prefs: {},
	setPref: async (_key, _value) => {},
});

export const UserPreferencesProvider = ({ children }) => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);
	const [loading, setLoading] = useState(true);
	const [prefs, setPrefs] = useState({});

	const load = useCallback(async () => {
		if (!employeeID || !accounts?.[0]) return;
		setLoading(true);
		try {
			const res = await getAPICall(
				instance,
				accounts[0],
				"/api/profile/preferences",
				{ employeeID }
			);
			setPrefs(res?.data || {});
		} finally {
			setLoading(false);
		}
	}, [employeeID, instance, accounts]);

	useEffect(() => {
		load();
	}, [load]);

	const setPref = useCallback(
		async (key, value) => {
			if (!accounts?.[0] || !employeeID) return;

			// optimistic update
			setPrefs((p) => ({ ...p, [key]: !!value }));

			try {
				await putAPICall(
					instance,
					accounts[0],
					"/api/profile/preferences",
					{
						employeeID,
						modifiedBy: employeeID,
						[key]: !!value,
					}
				);
			} catch (e) {
				// roll back if it failed
				setPrefs((p) => ({ ...p, [key]: !value }));
				throw e;
			}
		},
		[employeeID, instance, accounts]
	);

	const value = useMemo(
		() => ({ loading, prefs, setPref }),
		[loading, prefs, setPref]
	);

	return (
		<UserPreferencesContext.Provider value={value}>
			{children}
		</UserPreferencesContext.Provider>
	);
};
